import { NgForOf, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, Output, EventEmitter, ViewChild, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    EMPTY_ARRAY,
    TUI_DEFAULT_MATCHER,
    TuiAutoFocus,
    TuiFilterPipe,
    TuiLet,
} from '@taiga-ui/cdk';
import {
    TuiDataList,
    tuiIsEditingKey,
    TuiTextfield,
    TuiTextfieldDirective,
} from '@taiga-ui/core';
import { TuiMultiSelectModule } from '@taiga-ui/legacy';
import { ApiService } from '../../shared/services/api.service';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';

interface Items<T> {
    readonly items: readonly T[];
    readonly name: string;
}

interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    type: string;
    role_id: string;
    org_id: string;
    status: boolean;
    parkingId: string | null;
}

@Component({
    standalone: true,
    exportAs: "Examplecustom-list",
    selector: 'custom-list',
    imports: [
        FormsModule,
        NgForOf,
        NgIf,
        TuiAutoFocus,
        TuiDataList,
        TuiFilterPipe,
        TuiLet,
        TuiMultiSelectModule,
        TuiTextfield,
    ],
    templateUrl: './custom-list.component.html',
    styleUrls: ['./custom-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomListComponent<T> implements OnInit, OnChanges {
    @ViewChild(TuiTextfieldDirective, { read: ElementRef })
    private readonly input?: ElementRef<HTMLInputElement>;

    private readonly apiService = inject(ApiService);
    private readonly cdr = inject(ChangeDetectorRef);

    protected value: string = '';
    protected readonly all = EMPTY_ARRAY;
    protected readonly filter: (item: T, value: string) => boolean = TUI_DEFAULT_MATCHER;

    @Input()
    public items: ReadonlyArray<Items<T>> = [];

    /** When true, load users scoped to the given parking instead of the whole organization. */
    @Input()
    public useParkingUsers = false;

    @Input()
    public parkingId: string | null = null;

    @Output()
    public addTeamClick = new EventEmitter<void>();

    @Output()
    public userSelected = new EventEmitter<User>();

    // User list from API
    protected users: User[] = [];
    protected isLoading = false;
    protected error: string | null = null;

    protected currentUser: Partial<User> | null = null;
    protected currentUserLabel: string = 'My Self';

    get filteredUsers(): User[] {
        if (!this.value) {
            return this.users;
        }
        const search = this.value.toLowerCase().trim();
        return this.users.filter(user => {
            const name = this.getUserDisplayName(user).toLowerCase();
            const contact = (this.getUserContact(user) || '').toLowerCase();
            const role = (this.getUserRole(user) || '').toLowerCase();
            return name.includes(search) || contact.includes(search) || role.includes(search);
        });
    }

    protected matchesSearch(text: string | null | undefined): boolean {
        if (!this.value || !text) return true;
        return text.toLowerCase().includes(this.value.toLowerCase().trim());
    }

    protected onKeyDown(key: string): void {
        if (tuiIsEditingKey(key)) {
            this.input?.nativeElement.focus({ preventScroll: true });
        }
    }

    protected onMySelfClick(): void {
        const user = this.currentUser;
        if (user) {
            const u = user as any;
            // Emit a minimal User object compatible with consumer
            this.userSelected.emit({
                id: (user.id as string) || '',
                email: (user.email as string) || '',
                firstName: (user.firstName as string) || '',
                lastName: (user.lastName as string) || '',
                phone: u.phone || u.mobile || u.phoneNumber || u.phone_number || u.mobile_number || u.mobileNo || u.phoneNo || u.contact || '',
                type: u.type || 'user',
                role_id: u.role_id || '',
                org_id: u.org_id || '',
                status: true,
                parkingId: null,
            });
        }
    }

    protected onAddTeamClick(): void {
        console.log('Add Team clicked');
        this.addTeamClick.emit();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['parkingId'] || changes['useParkingUsers']) && this.useParkingUsers && this.parkingId) {
            this.loadUsers();
        }
    }

    ngOnInit(): void {
        this.loadUsers();
        // Load current user and set label
        try {
            const me: any = this.apiService.getCurrentUser?.();
            if (me) {
                this.currentUser = me;
                const label = this.getUserDisplayName(me);
                if (label && label.trim().length > 0) {
                    this.currentUserLabel = label;
                }
            }
        } catch { }
    }

    private loadUsers(): void {
        this.isLoading = true;
        this.error = null;

        if (this.useParkingUsers && this.parkingId) {
            this.loadParkingUsers(this.parkingId);
            return;
        }

        // Get org_id from localStorage
        const orgId = this.apiService.getCurrentOrganizationId();

        if (!orgId) {
            this.error = 'Organization ID not found. Please log in again.';
            this.isLoading = false;
            this.users = [];
            this.mergeLocalSavedUsers();
            if (this.users.length > 0) {
                this.error = null;
            }
            this.cdr.markForCheck();
            console.error('No organization ID found');
            return;
        }

        console.log('Loading users for organization:', orgId);

        // Use getAllOrganizationUsers which handles response format better
        this.apiService.getAllOrganizationUsers(orgId).pipe(
            tap((response: any) => {
                console.log('Raw API response:', response);
                console.log('Response type:', typeof response);
                console.log('Is array:', Array.isArray(response));

                // Handle different response formats
                let users: User[] = [];
                if (Array.isArray(response)) {
                    users = response;
                    console.log('Response is direct array, length:', users.length);
                } else if (response && Array.isArray(response.data)) {
                    users = response.data;
                    console.log('Response has data array, length:', users.length);
                } else if (response && response.success && Array.isArray(response.data)) {
                    users = response.data;
                    console.log('Response has success.data array, length:', users.length);
                } else if (response && response.users && Array.isArray(response.users)) {
                    users = response.users;
                    console.log('Response has users array, length:', users.length);
                } else {
                    console.warn('️ Unexpected response format:', response);
                }

                console.log('All users before filtering:', users.length, users);

                // Filter only active users (status === true or status is undefined/null)
                // Some APIs might not have status field, so include those too
                this.users = users.filter(user => {
                    if (!user) return false;
                    // Include users with status === true or no status field
                    const hasStatus = user.hasOwnProperty('status');
                    if (!hasStatus) {
                        console.log('User without status field included:', user);
                        return true;
                    }
                    // Handle boolean status or string 'active' (API might return different types)
                    // Cast user to any to handle different API response formats
                    const userAny = user as any;
                    const statusValue = userAny.status;
                    const isActive = statusValue === true ||
                        (typeof statusValue === 'string' && statusValue === 'active') ||
                        (typeof statusValue === 'number' && statusValue === 1);
                    if (isActive) {
                        console.log('Active user:', user);
                    } else {
                        console.log('Inactive user filtered out:', user, 'status:', user.status);
                    }
                    return isActive;
                });

                this.mergeLocalSavedUsers();
                this.isLoading = false;
                console.log('Final users loaded:', this.users.length, 'active users');
                console.log('Users list:', this.users);

                // Trigger change detection for OnPush strategy
                this.cdr.markForCheck();
            }),
            catchError((error) => {
                this.error = 'Failed to load users. Please try again.';
                this.isLoading = false;
                this.users = [];
                this.mergeLocalSavedUsers();
                console.error('Error loading users:', error);
                console.error('Error details:', JSON.stringify(error, null, 2));
                this.cdr.markForCheck();
                return of([]);
            })
        ).subscribe();
    }

    private loadParkingUsers(parkingId: string): void {
        this.apiService.getParkingUsers(parkingId).pipe(
            tap((response: any) => {
                let users: User[] = [];
                if (Array.isArray(response)) {
                    users = response;
                } else if (response?.users && Array.isArray(response.users)) {
                    users = response.users;
                } else if (response?.data && Array.isArray(response.data)) {
                    users = response.data;
                } else if (response?.success && Array.isArray(response.data)) {
                    users = response.data;
                }

                this.users = users.filter(user => {
                    if (!user) return false;
                    const userAny = user as any;
                    if (!userAny.hasOwnProperty('status')) return true;
                    const statusValue = userAny.status;
                    return statusValue === true ||
                        (typeof statusValue === 'string' && statusValue === 'active') ||
                        (typeof statusValue === 'number' && statusValue === 1);
                });

                this.mergeLocalSavedUsers();
                this.isLoading = false;
                this.cdr.markForCheck();
            }),
            catchError((error) => {
                this.error = 'Failed to load parking team members. Please try again.';
                this.isLoading = false;
                this.users = [];
                this.mergeLocalSavedUsers();
                console.error('Error loading parking users:', error);
                this.cdr.markForCheck();
                return of([]);
            })
        ).subscribe();
    }

    /**
     * Include locally deferred manager / invite so they appear in the dropdown
     * before Go Live creates them on the server.
     */
    private mergeLocalSavedUsers(): void {
        const localUsers = this.getLocalSavedUsers();
        if (!localUsers.length) {
            return;
        }

        const merged = [...this.users];
        for (const localUser of localUsers) {
            if (!this.isUserAlreadyInList(merged, localUser)) {
                merged.unshift(localUser);
            }
        }
        this.users = merged;
    }

    private getLocalSavedUsers(): User[] {
        const result: User[] = [];
        const seenKeys = new Set<string>();

        const pushUnique = (user: User | null) => {
            if (!user) return;
            const key = this.getUserDedupKey(user);
            if (!key || seenKeys.has(key)) return;
            seenKeys.add(key);
            result.push(user);
        };

        try {
            const teamRaw = localStorage.getItem('parking_team_member_data');
            if (teamRaw) {
                const parsed = JSON.parse(teamRaw);
                pushUnique(this.normalizeLocalUser(parsed?.selectedTeamMember));
            }
        } catch { /* ignore bad local data */ }

        try {
            const pendingRaw = localStorage.getItem('parking_pending_team_invite');
            if (pendingRaw) {
                const pending = JSON.parse(pendingRaw);
                const display = pending?.display || {};
                const userData = pending?.payload?.userData || {};
                pushUnique(this.normalizeLocalUser({
                    id: display.userId || userData.id || '',
                    name: display.name || userData.name || '',
                    email: display.email || userData.email || '',
                    phone: display.phone || userData.phone || '',
                    type: display.roleType || userData.type || '',
                    role: { name: display.roleType || userData.type || '' },
                    roleType: display.roleType || userData.type || '',
                    pendingInvite: true,
                    status: true,
                }));
            }
        } catch { /* ignore bad local data */ }

        return result;
    }

    private normalizeLocalUser(raw: any): User | null {
        if (!raw || typeof raw !== 'object') {
            return null;
        }

        const name = (raw.name || raw.fullName || '').trim();
        const firstName = (raw.firstName || '').trim() || (name ? name.split(/\s+/)[0] : '');
        const lastName = (raw.lastName || '').trim() ||
            (name ? name.split(/\s+/).slice(1).join(' ') : '');
        const email = (raw.email || '').trim();
        const phone = (
            raw.phone ||
            raw.mobile ||
            raw.phoneNumber ||
            raw.phone_number ||
            ''
        ).toString().trim();
        const id = (raw.id || raw.user_id || raw.userId || '').toString().trim();

        // Need at least some identity to show in the list
        if (!id && !email && !phone && !name && !firstName) {
            return null;
        }

        const roleName = raw.role?.name || raw.role_name || raw.roleType || raw.type || '';

        return {
            id: id || `local-${email || phone || name || firstName}`,
            email,
            firstName,
            lastName,
            phone,
            type: roleName || raw.type || 'user',
            role_id: raw.role_id || '',
            org_id: raw.org_id || '',
            status: true,
            parkingId: raw.parkingId ?? null,
            ...(name ? { name } : {}),
            ...(roleName ? { role: { name: roleName }, roleType: roleName } : {}),
            ...(raw.pendingInvite ? { pendingInvite: true } : {}),
        } as User;
    }

    private getUserDedupKey(user: Partial<User> | any): string {
        const id = (user?.id || user?.user_id || user?.userId || '').toString().trim().toLowerCase();
        if (id && !id.startsWith('local-')) return `id:${id}`;
        const email = (user?.email || '').toString().trim().toLowerCase();
        if (email) return `email:${email}`;
        const phone = (user?.phone || user?.mobile || '').toString().trim().toLowerCase();
        if (phone) return `phone:${phone}`;
        const name = this.getUserDisplayName(user as User).toLowerCase();
        return name ? `name:${name}` : '';
    }

    private isUserAlreadyInList(users: User[], candidate: User): boolean {
        const candidateKey = this.getUserDedupKey(candidate);
        if (!candidateKey) return false;
        return users.some(user => this.getUserDedupKey(user) === candidateKey);
    }

    // Expose a public refresh method so parents can reload the users after creating a team member
    public refresh(): void {
        console.log('Refreshing users list...');
        this.loadUsers();
    }

    protected onUserSelect(user: User): void {
        console.log('User selected:', user);
        this.userSelected.emit(user);
    }

    protected getUserContact(user: Partial<User> | null | undefined): string {
        if (!user) return '';
        const u = user as any;
        const email = (u.email || '').trim();
        if (email) return email;
        return (
            u.phone ||
            u.mobile ||
            u.phoneNumber ||
            u.phone_number ||
            u.mobile_number ||
            u.mobileNo ||
            u.phoneNo ||
            u.contact ||
            ''
        ).toString().trim();
    }

    protected getUserRole(user: Partial<User> | null | undefined): string {
        if (!user) return '';
        const u = user as any;
        const role =
            u.role?.name ||
            u.role_name ||
            u.current_role ||
            u.type ||
            (typeof u.role === 'string' ? u.role : '') ||
            '';
        const cleaned = String(role).trim().replace(/[_-]+/g, ' ');
        if (!cleaned) return '';
        return cleaned.replace(/\b\w/g, (c: string) => c.toUpperCase());
    }

    protected getUserMeta(user: Partial<User> | null | undefined): string {
        const parts = [this.getUserContact(user), this.getUserRole(user)].filter(Boolean);
        return parts.join(' • ');
    }

    protected getUserDisplayName(user: User): string {
        // Handle different API response formats - check for name fields
        const userAny = user as any;

        // Debug: Log user object to see available fields
        console.log('getUserDisplayName - user object:', user);
        console.log('Available fields:', Object.keys(userAny));

        // Check for fullName or name field first
        if (userAny.fullName) {
            console.log('Using fullName:', userAny.fullName);
            return userAny.fullName.trim();
        }
        if (userAny.name) {
            console.log('Using name:', userAny.name);
            return userAny.name.trim();
        }

        // Check for firstName and lastName
        const firstName = user.firstName || userAny.firstName || '';
        const lastName = user.lastName || userAny.lastName || '';

        console.log('firstName:', firstName, 'lastName:', lastName);

        if (firstName || lastName) {
            const fullName = `${firstName} ${lastName}`.trim();
            if (fullName) {
                console.log('Using firstName + lastName:', fullName);
                return fullName;
            }
        }

        // Fallback to email if no name found
        const email = user.email || userAny.email || '-';
        console.log('️ No name found, using email:', email);
        return email;
    }
}
